import { ResponsiveRadialBar } from "@nivo/radial-bar";
import { FC } from "react";
import { cx } from "shared/utils";
import { ScoreGraphSize } from "./constants";
import { DecisionAction } from "shared/types/application";
import { toFixedFloored } from "utils/toFixedFloored";

type Props = {
  size?: ScoreGraphSize;
  subtitle?: string;
  score?: number | null;
  recommendation?: DecisionAction | null;
  declineRangeEnd?: number;
  conditionalApprovalRangeEnd?: number;
};

const graphSize = {
  [ScoreGraphSize.XS]: "h-[58px] w-[58px] top-3",
  [ScoreGraphSize.SM]: "h-[80px] w-[80px]",
  [ScoreGraphSize.LG]: "h-[200px] w-[230px]"
};

export const ScoreGraph: FC<Props> = ({
  size = ScoreGraphSize.LG,
  subtitle,
  score: _score = 0,
  recommendation,
  declineRangeEnd,
  conditionalApprovalRangeEnd
}) => {
  const score =
    _score === null
      ? "-"
      : String(
          _score === 10 ? 10 : _score === 0 ? 0 : toFixedFloored(_score, 1)
        );

  const data = [
    {
      id: "values",
      data: [
        {
          x: _score === null ? "-" : score,
          y: _score === null ? 0 : _score
        }
      ]
    }
  ];

  return (
    <div className={cx("relative", graphSize[size])}>
      <ResponsiveRadialBar
        data={data}
        maxValue={10}
        startAngle={-90}
        endAngle={90}
        innerRadius={0.8}
        cornerRadius={15}
        enableRadialGrid={false}
        enableCircularGrid={false}
        isInteractive={false}
        radialAxisStart={{
          tickComponent: () => null
        }}
        circularAxisOuter={{ tickComponent: () => null }}
        defs={[
          {
            id: "gradientA",
            type: "linearGradient",
            colors: [
              { offset: 100, color: "#FF737A" },
              { offset: 0, color: "#E6000B" }
            ]
          },
          {
            id: "gradientB",
            type: "linearGradient",
            colors: [
              { offset: 25.5, color: "#FFD993" },
              { offset: 68.63, color: "#F4A107" }
            ]
          },
          {
            id: "gradientC",
            type: "linearGradient",
            colors: [
              { offset: 11.25, color: "#52C535" },
              { offset: 96.25, color: "#69E949" }
            ]
          }
        ]}
        fill={[
          {
            match: ({ value }: any) =>
              (recommendation && recommendation === DecisionAction.Decline) ||
              (!recommendation && value <= (declineRangeEnd || 5)),
            id: "gradientA"
          },
          {
            match: ({ value }: any) =>
              (recommendation &&
                recommendation === DecisionAction.ConditionalApprove) ||
              (!recommendation &&
                value > (declineRangeEnd || 5) &&
                value <= (conditionalApprovalRangeEnd || 9)),
            id: "gradientB"
          },
          {
            match: ({ value }: any) =>
              (recommendation && recommendation === DecisionAction.Approve) ||
              (!recommendation && value > (conditionalApprovalRangeEnd || 9)),
            id: "gradientC"
          }
        ]}
        tracksColor="rgba(28,29,34,0.040)"
      />

      {size === ScoreGraphSize.XS && (
        <div className="absolute top-3 w-[58px]">
          <div className="flex flex-col items-center">
            <p className="text-sm font-bold text-content-1000">{score}</p>
            {subtitle ? (
              <p className="text-center text-xs text-content-500">{subtitle}</p>
            ) : null}
          </div>
        </div>
      )}

      {size === ScoreGraphSize.SM && (
        <div className="absolute top-5 w-20">
          <div className="flex flex-col items-center">
            <p className="text-xl font-bold text-content-1000">{score}</p>
            {subtitle ? (
              <p className="text-center text-xs text-content-500">{subtitle}</p>
            ) : null}
          </div>
        </div>
      )}

      {size === ScoreGraphSize.LG && (
        <div className="absolute left-9 top-11 w-40">
          <div className="flex flex-col items-center">
            <p className="mb-6 h-[54px] text-[54px] font-bold text-content-1000">
              {score}
            </p>
            {subtitle ? (
              <p className="text-sm text-content-400">{subtitle}</p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};
